import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t, Trans } from "@lingui/macro";
import { ArrowCounterClockwise, DotsSixVertical, Plus, TrashSimple } from "@phosphor-icons/react";
import { defaultMetadata, defaultLayout, lftsLayout } from "@reactive-resume/schema";
import { Button, Portal, Tooltip } from "@reactive-resume/ui";
import {
  cn,
  LayoutLocator,
  moveItemInLayout,
  parseLayoutLocator,
  SortablePayload,
} from "@reactive-resume/utils";
import get from "lodash.get";
import { useEffect, useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { getSectionIcon } from "../shared/section-icon";

type ColumnProps = {
  id: string;
  name: string;
  items: string[];
  isDragEnabled: boolean;
};

const Column = ({ id, name, items, isDragEnabled }: ColumnProps) => {
  const { setNodeRef } = useDroppable({ id });
  const currentTemplate = useResumeStore((state) => state.resume.data?.metadata.template);

  return (
    <SortableContext id={id} items={items} strategy={verticalListSortingStrategy}>
      <div className="relative">
        <div className="absolute inset-0 w-3/4 rounded bg-secondary/50" />

        <div className="relative z-10 p-3 pb-8">
          {currentTemplate !== "mastersunion" && <p className="mb-3 text-xs font-bold">{name}</p>}

          <div ref={setNodeRef} className="space-y-3">
            {items.map((section) => (
              <SortableSection key={section} id={section} isDragEnabled={isDragEnabled} />
            ))}
          </div>
        </div>
      </div>
    </SortableContext>
  );
};

type SortableSectionProps = {
  id: string;
  isDragEnabled: boolean;
};

const SortableSection = ({ id, isDragEnabled }: SortableSectionProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transition,
    opacity: isDragging ? 0.5 : 1,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div ref={setNodeRef} style={style} {...(isDragEnabled ? { ...listeners, ...attributes } : {})}>
      <Section id={id} isDragEnabled={isDragEnabled} />
    </div>
  );
};

type SectionProps = {
  id: string;
  isDragging?: boolean;
  isDragEnabled: boolean;
};

const Section = ({ id, isDragging = false, isDragEnabled }: SectionProps) => {
  const name = useResumeStore((state) =>
    get(state.resume.data.sections, `${id}.name`, id),
  ) as string;
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);
  const displayName =
    id === "awards" && currentTemplate === "mastersunion"
      ? "Achievements"
      : name;

  return (
    <div
      className={cn(
        "rounded bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary-accent",
        isDragEnabled && "cursor-grab",
        isDragging && isDragEnabled && "cursor-grabbing",
      )}
    >
      <div className="flex items-center gap-x-2">
        {isDragEnabled && <DotsSixVertical size={12} weight="bold" />}
        <p className="flex-1 truncate text-xs font-medium">{displayName}</p>
      </div>
    </div>
  );
};

export const LayoutSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const layout = useResumeStore((state) => state.resume.data.metadata.layout);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);

  const [activeId, setActiveId] = useState<string | null>(null);

  // Enable drag & drop only for advanced/custom templates
  const isAdvancedTemplate =
    currentTemplate === "Create Custom" ||
    currentTemplate === "mastersunion" ||
    currentTemplate === "lpublackcustom";

  const getBaseLayout = () =>
    currentTemplate === "LFTS 1(GEN)" || currentTemplate === "LFTS 2(SPL)"
      ? lftsLayout
      : defaultLayout;

  // Non-advanced templates reset layout to the template default. Base layouts do not list
  // `custom.*` keys, so without merging, custom sections stay in `sections.custom` (sidebar)
  // but disappear from the preview. Re-append custom keys like `addSection` (last page, main column).
  useEffect(() => {
    if (!isAdvancedTemplate) {
      const baseLayout = JSON.parse(JSON.stringify(getBaseLayout())) as string[][][];
      const { resume } = useResumeStore.getState();
      const customRecord = resume.data?.sections?.custom ?? {};
      const fromData = Object.keys(customRecord).map((id) => `custom.${id}`);
      const fromLayout = new Set<string>();
      for (const page of resume.data?.metadata?.layout ?? []) {
        for (const column of page) {
          for (const key of column) {
            if (key.startsWith("custom.")) fromLayout.add(key);
          }
        }
      }
      const customKeys = [...new Set([...fromData, ...fromLayout])];
      if (customKeys.length > 0) {
        const lastPageIndex = baseLayout.length - 1;
        if (!baseLayout[lastPageIndex]) baseLayout[lastPageIndex] = [[], []];
        if (!baseLayout[lastPageIndex][0]) baseLayout[lastPageIndex][0] = [];
        const mainCol = baseLayout[lastPageIndex][0];
        for (const key of customKeys) {
          if (!mainCol.includes(key)) mainCol.push(key);
        }
      }
      setValue("metadata.layout", baseLayout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdvancedTemplate, currentTemplate]);

  useEffect(() => {
    const baseLayout = getBaseLayout();
    const currentSections = new Set(layout.flatMap((page) => page.flatMap((column) => column)));
    let changed = false;

    const layoutCopy = JSON.parse(JSON.stringify(layout)) as string[][][];

    baseLayout.forEach((page, pageIndex) => {
      if (!layoutCopy[pageIndex]) layoutCopy[pageIndex] = [[], []];

      page.forEach((column, columnIndex) => {
        if (!layoutCopy[pageIndex][columnIndex]) layoutCopy[pageIndex][columnIndex] = [];

        column.forEach((section) => {
          if (currentSections.has(section)) return;
          layoutCopy[pageIndex][columnIndex].push(section);
          changed = true;
        });
      });
    });

    if (changed) {
      setValue("metadata.layout", layoutCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, currentTemplate]);

  useEffect(() => {
    if (currentTemplate !== "mastersunion") return;

    const layoutCopy = JSON.parse(JSON.stringify(layout)) as string[][][];

    let changed = false;

    layoutCopy.forEach((page) => {
      if (page[1]?.length) {
        page[0].push(...page[1]);
        page[1] = [];
        changed = true;
      }
    });

    if (changed) {
      setValue("metadata.layout", layoutCopy);
    }
  }, [layout, currentTemplate]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    if (!isAdvancedTemplate) return;
    setActiveId(active.id as string);
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const onDragEvent = ({ active, over }: DragOverEvent | DragEndEvent) => {
    if (!isAdvancedTemplate || !over || !active.data.current) return;

    const currentPayload = active.data.current.sortable as SortablePayload | null;
    const current = parseLayoutLocator(currentPayload);

    if (active.id === over.id) return;

    if (!over.data.current) {
      const [page, column] = (over.id as string).split(".").map(Number);
      const target = { page, column, section: 0 } as LayoutLocator;

      const newLayout = moveItemInLayout(current, target, layout);
      setValue("metadata.layout", newLayout);

      return;
    }

    const targetPayload = over.data.current.sortable as SortablePayload | null;
    const target = parseLayoutLocator(targetPayload);

    const newLayout = moveItemInLayout(current, target, layout);
    setValue("metadata.layout", newLayout);
  };

  const onDragEnd = (event: DragEndEvent) => {
    onDragEvent(event);
    setActiveId(null);
  };

  const onAddPage = () => {
    if (!isAdvancedTemplate) return;
    
    const layoutCopy = JSON.parse(JSON.stringify(layout)) as string[][][];

    layoutCopy.push([[], []]);

    setValue("metadata.layout", layoutCopy);
  };

  const onRemovePage = (page: number) => {
    if (!isAdvancedTemplate) return;

    const layoutCopy = JSON.parse(JSON.stringify(layout)) as string[][][];

    layoutCopy[0][0].push(...layoutCopy[page][0]); // Main
    layoutCopy[0][1].push(...layoutCopy[page][1]); // Sidebar

    layoutCopy.splice(page, 1);

    setValue("metadata.layout", layoutCopy);
  };

  const onResetLayout = () => {
    const layoutCopy = JSON.parse(JSON.stringify(getBaseLayout())) as string[][][];

    // Loop through all pages and columns, and get any sections that start with "custom."
    // These should be appended to the first page of the new layout.
    const customSections: string[] = [];

    layout.forEach((page) => {
      page.forEach((column) => {
        customSections.push(...column.filter((section) => section.startsWith("custom.")));
      });
    });

    if (customSections.length > 0) layoutCopy[0][0].push(...customSections);

    setValue("metadata.layout", layoutCopy);
  };

  return (
    <section id="layout" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          {getSectionIcon("layout")}
          <h2 className="line-clamp-1 text-3xl font-bold">{t`Layout`}</h2>
        </div>

        <Tooltip content={t`Reset Layout`}>
          <Button size="icon" variant="ghost" onClick={onResetLayout}>
            <ArrowCounterClockwise />
          </Button>
        </Tooltip>
      </header>

      <main className="grid gap-y-4">
        {/* Pages */}
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
          collisionDetection={closestCenter}
        >
          {layout.map((page, pageIndex) => {
            const mainIndex = `${pageIndex}.0`;
            const sidebarIndex = `${pageIndex}.1`;

            const main = page[0];
            const sidebar = page[1];

            return (
              <div key={pageIndex} className="rounded border p-3 pb-4">
                <div className="flex items-center justify-between">
                  <p className="mb-3 text-xs font-bold">
                    <Trans>Page {pageIndex + 1}</Trans>
                  </p>

                  {pageIndex !== 0 && isAdvancedTemplate && (
                    <Tooltip content={t`Remove Page`}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        onClick={() => onRemovePage(pageIndex)}
                      >
                        <TrashSimple size={12} className="text-error" />
                      </Button>
                    </Tooltip>
                  )}
                </div>

                <div className={cn("grid items-start gap-x-4", currentTemplate === "mastersunion" ? "grid-cols-1" : "grid-cols-2")}>
                  <Column id={mainIndex} name={t`Main`} items={main} isDragEnabled={isAdvancedTemplate} />
                  {currentTemplate !== "mastersunion" && (
                    <Column id={sidebarIndex} name={t`Sidebar`} items={sidebar} isDragEnabled={isAdvancedTemplate} />
                  )}
                </div>
              </div>
            );
          })}

          <Portal>
            <DragOverlay>{activeId && <Section id={activeId} isDragging isDragEnabled={isAdvancedTemplate} />}</DragOverlay>
          </Portal>
        </DndContext>

        {isAdvancedTemplate && (
          <Button variant="outline" className="ml-auto" onClick={onAddPage}>
            <Plus />
            <span className="ml-2">{t`Add New Page`}</span>
          </Button>
        )}
      </main>
    </section>
  );
};
